import { useSyncExternalStore } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UserCog } from '../../icons';
// 회사 관리 (2026-08-25) — 관리 콘솔(8444)에서 회사 관리자가 쓰는 화면들을 복사해 왔다.
import { CompanyAdminPanel } from './CompanyAdminPanel';
import { useAuthStore } from '../../store/authStore';
import { tSettings, getSettingsLang, subscribeSettingsLang } from '../../lib/settings-i18n';
// 손짠 머리 → 정본 PageHeader(2026-10-17 · web 레인 W7c — 쪽 머리 한 꼴 F4)
import { PageHeader } from '../../components/shared';
import './SettingsPage.css';

/**
 * 통합설정 — **회사 관리자 화면**. 라우트: /settings (router/index.tsx).
 *
 * ── 2026-08-25 (사용자 지시) — 개인 설정 세 탭을 걷어냈다 ──────────────────────
 * 이 화면에는 원래 개인 설정 세 탭이 있었다(일반 · 프로필 · 알림 연동). 그 셋은 지금
 * **내 프로필과 설정 팝업에서 사용자별로 관리한다** — 자리가 둘이면 사람마다 다른 곳을 보고,
 * 한쪽만 고쳐지면 둘이 갈라진다. 그래서 통합설정은 **회사 관리자 화면 한 가지**만 남긴다.
 *
 * 옮겨 간 곳(이미 그렇게 돌고 있던 것을 확인만 했다 — 새로 만든 것이 아니다):
 *   · 일반(테마·다크·언어·알림)  → 설정 팝업 › 환경(화면·외관 / 테마 / 언어) + 알림 › 종류
 *   · 프로필 + 비밀번호           → 내 프로필 › 프로필 · 보안, 설정 팝업 › 계정 / 보안 › 비밀번호
 *   · 알림 연동(외부 push)        → 설정 팝업 › 알림 › **외부 연동** — 같은 컴포넌트를 그대로 건다
 *     (`SettingsModal.tsx` 가 `ExternalProfileSection` 을 그대로 렌더한다)
 *
 * ⚠ 걷어낸 것은 **이 화면의 렌더 경로뿐**이다. `EnvironmentSection` · `ProfileSection` ·
 *   `PasswordSection` · `ExternalProfileSection` 파일은 무수정으로 남겨 뒀다 — 뒤의 셋은
 *   설정 팝업·내 프로필이 지금도 쓰고, `EnvironmentSection` 은 롤백 대비로 남긴다
 *   (`NotifKindsField.tsx` 머리말이 이미 "원본은 무수정으로 남겨 둔다" 고 적어 뒀다).
 *
 * ── 권한 ──────────────────────────────────────────────────────────────────
 * 좌측 레일의 '통합설정' 자체가 관리자에게만 보이지만(NavBar), **라우트에는 RoleGuard 가 없어**
 * 주소로는 누구나 들어온다. 그래서 직원에게는 막다른 빈 화면 대신 **내 프로필로 가는 길**을
 * 보여 준다. 권위 있는 차단은 서버가 한다(콘솔에서 온 라우트는 전부 ADMIN/SYSTEM_ADMIN).
 */
export function SettingsPage() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'ADMIN' || role === 'SYSTEM_ADMIN';
  // ★ 2026-09-14 (사업장 v3) — 칸을 주소로 연다(`?pane=worksite`). 메뉴를 누르면 주소도 따라 바꿔, 상세에서 「목록으로」 가 제 칸으로 온다.
  const [params, setParams] = useSearchParams();

  return (
    <div className="settings-page">
      {/* 다른 화면과 동일한 헤더바 패턴 — surface bg + border-bottom.
          ★ 2026-08-26 — **관리자 화면에는 이 머리를 두지 않는다.** 설정 트리가 제 머리에
            «통합설정» 을 이게 되면서(CompanyAdminPanel) 같은 제목이 한 화면에 두 번 뜬다.
            시안도 페이지 머리 없이 트리와 본문이 제 머리를 각자 인다. 걷어낸 김에 트리가
            화면 꼭대기부터 서서 «칸» 으로 더 또렷하게 읽힌다.
            직원이 보는 안내 화면은 종전 그대로 이 머리를 인다. */}
      {!isAdmin && (
        <PageHeader
          breadcrumb={[tSettings('settings.company.heading')]}
          title={tSettings('settings.company.heading')}
          description={tSettings('settings.company.subtitle')}
        />
      )}

      <div className="settings-body scrollbar-thin">
        {isAdmin ? (
          /* 콘솔에서 온 화면이 3단이라 760px 에서는 짓눌린다 — 여기서는 폭을 푼다. */
          <div className="settings-content settings-content--wide">
            <CompanyAdminPanel
              pane={params.get('pane')}
              onPaneSelect={(id) => setParams(id === 'company' ? {} : { pane: id }, { replace: true })}
            />
          </div>
        ) : (
          <div className="settings-content">
            <section className="settings-personal" aria-labelledby="settings-personal-h">
              <span className="settings-personal__icon" aria-hidden><UserCog size={22} /></span>
              <h2 className="settings-personal__title" id="settings-personal-h">
                {tSettings('settings.personal.heading')}
              </h2>
              <p className="settings-personal__body">{tSettings('settings.personal.body')}</p>
              <Link className="settings-personal__go" to="/me">
                {tSettings('settings.personal.cta')}
              </Link>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
